import { DraggableAttributes, DraggableSyntheticListeners, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { AddCircle as PlusCircle } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChapterColumnHeader } from './chapter-column-header';
import { ChapterColumnMenu } from './chapter-column-menu';
import { SortableSceneCard } from './sortable-scene-card';

import { DeleteConfirmationModal } from '@/components/mui-delete-confirmation-modal';
import { useNovel } from '@/context/novel-context';
import { UnreadyScenesDialog } from '@/features/chapters/components/UnreadyScenesDialog';
import { useCreateChapter, useDeleteChapter, useUpdateChapter } from '@/features/chapters/hooks/use-mutation-hooks';
import { usePublishChapter } from '@/features/chapters/hooks/use-publish-chapter';
import { useFirebase } from '@/firebase';
import { EntitySummary, NarrativeSummary, TextSummary } from '@/lib/types';

export interface ChapterColumnProps {
  chapter: NarrativeSummary;
  scenes: NarrativeSummary[];
  characters: Record<string, EntitySummary>;
  locations: Record<string, EntitySummary>;
  organizations: Record<string, EntitySummary>;
  events: Record<string, EntitySummary>;
  contents: Record<string, TextSummary>;
  index: number;
  isEditing: boolean;
  setEditingId: (id: string | null) => void;
  filterFn: (scene: NarrativeSummary) => boolean;
  dragAttributes?: DraggableAttributes;
  dragListeners?: DraggableSyntheticListeners;
}

export function ChapterColumn({
  chapter,
  scenes,
  characters,
  locations,
  organizations,
  events,
  contents,
  isEditing,
  setEditingId,
  filterFn,
  dragAttributes,
  dragListeners
}: ChapterColumnProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useFirebase();
  const { novel } = useNovel();
  const { updateChapter } = useUpdateChapter(chapter.id);
  const { createChapter } = useCreateChapter();
  const { archiveChapter, deleteChapter, isDeleting } = useDeleteChapter(chapter.id);
  const { assembleFinalEdition, updatePublishStatus, isAssembling, unreadyData, setUnreadyData } = usePublishChapter();

  const [nameValue, setNameValue] = useState(chapter.name);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<'draft' | 'preview' | 'public' | null>(null);

  const filteredScenes = scenes.filter(filterFn);

  // Droppable container registration for dnd-kit
  const { setNodeRef } = useDroppable({
    id: chapter.id,
    data: {
      type: 'container',
      chapterId: chapter.id
    }
  });

  useEffect(() => {
    setNameValue(chapter.name);
  }, [chapter.name]);

  const handleSaveName = () => {
    if (nameValue.trim() && nameValue !== chapter.name) {
      updateChapter({ name: nameValue });
    } else {
      setNameValue(chapter.name);
    }
    setEditingId(null);
  };

  const handleUpdateStatus = async (status: 'draft' | 'preview' | 'public') => {
    if (pendingStatus) return;
    setPendingStatus(status);
    try {
      await updatePublishStatus(chapter.id, status);
    } finally {
      setPendingStatus(null);
    }
  };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minWidth: 320,
        width: 320,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        bgcolor: (theme) => {
          const isDark = theme.palette.mode === 'dark';
          const defaultBg = alpha(theme.palette.text.primary, isDark ? 0.04 : 0.025);
          if (chapter.id === 'unassigned') return defaultBg;

          const status = chapter.publishStatus || 'draft';
          if (status === 'public') return alpha(theme.palette.success.main, isDark ? 0.04 : 0.05);
          if (status === 'preview') return alpha(theme.palette.info.main, isDark ? 0.04 : 0.05);
          return defaultBg;
        },
        borderRadius: 4,
        p: 2,
        pt: chapter.id !== 'unassigned' ? 1.5 : 2,
        borderTop: (theme) => {
          if (chapter.id === 'unassigned') return 'none';
          const status = chapter.publishStatus || 'draft';
          if (status === 'preview') return `4px solid ${theme.palette.info.main}`;
          if (status === 'public') return `4px solid ${theme.palette.success.main}`;
          return `4px solid ${theme.palette.text.disabled}`;
        },
        minHeight: '600px',
        transition: 'all 0.2s ease'
      }}
    >
      <ChapterColumnHeader
        chapter={chapter}
        scenesCount={scenes.length}
        isEditing={isEditing}
        nameValue={nameValue}
        setNameValue={setNameValue}
        onSaveName={handleSaveName}
        setEditingId={setEditingId}
        onMenuOpen={(e) => setMenuAnchor(e.currentTarget)}
        onCreateNextChapter={async () => {
          const id = await createChapter(undefined, (chapter.order || 0) + 1);
          if (id) setEditingId(id);
        }}
        onOpenEditor={chapter.id === 'unassigned' ? undefined : async () => {
          if (isAssembling) return;
          if (chapter.mainContentId) {
            await navigate({ to: `/publish-editor/${chapter.id}` });
            return;
          }
          await assembleFinalEdition(chapter.id);
        }}
        isOpeningEditor={isAssembling}
        isChangingStatus={pendingStatus !== null}
        dragAttributes={dragAttributes}
        dragListeners={dragListeners}
      />

      <SortableContext items={filteredScenes.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <Stack spacing={2} sx={{ flexGrow: 1 }}>
          {filteredScenes.map((scene) => (
            <SortableSceneCard
              key={scene.id}
              scene={scene}
              characters={characters}
              locations={locations}
              organizations={organizations}
              events={events}
              contents={contents}
            />
          ))}

          {chapter.id !== 'unassigned' && (
            <Box
              id="add-scene-step"
              component={Link}
              to={`/scenes/new?chapterId=${chapter.id}`}
              sx={{
                height: 80,
                borderRadius: 3,
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                color: 'text.secondary',
                transition: 'all 0.2s ease',
                mt: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main',
                  color: 'text.primary',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <PlusCircle sx={{ fontSize: 24, opacity: 0.6 }} />
                <Typography variant="button">
                  {t('entity_list.scenes.add_new')}
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>
      </SortableContext>

      <ChapterColumnMenu
        chapter={chapter}
        menuAnchor={menuAnchor}
        onMenuClose={() => setMenuAnchor(null)}
        onRename={() => setEditingId(chapter.id)}
        onArchive={archiveChapter}
        onDelete={() => setShowDeleteConfirm(true)}
        onUpdateStatus={handleUpdateStatus}
        onAssemble={() => assembleFinalEdition(chapter.id)}
        hasScenes={scenes.length > 0}
        canPublish={user?.uid === novel?.ownerId}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          await deleteChapter();
          setShowDeleteConfirm(false);
        }}
        isDeleting={isDeleting}
        itemName={chapter.name}
        description={t('chapters.delete_warning_desc')}
      />
      <UnreadyScenesDialog
        isOpen={!!unreadyData}
        scenes={unreadyData?.scenes || []}
        onClose={() => setUnreadyData(null)}
        onConfirm={async () => {
          if (unreadyData) {
            await assembleFinalEdition(unreadyData.chapterId, true);
            setUnreadyData(null);
          }
        }}
        isAssembling={isAssembling}
      />
    </Box>
  );
}
